import { Injectable }   from '@angular/core';
import { HttpClient }   from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap }          from 'rxjs/operators';
import { environment }  from 'src/app/environments/environment';
import { AiInsight, AiInsightsResponse } from '../ai-insights/models/ai-insight.model';

@Injectable({ providedIn: 'root' })
export class AiInsightsService {

  private api = `${environment.apiUrl}/api/ai-insights`;

  private _unreadCount$ = new BehaviorSubject<number>(0);
  private _insights$    = new BehaviorSubject<AiInsight[]>([]);

  unreadCount$ = this._unreadCount$.asObservable();
  insights$    = this._insights$.asObservable();

  constructor(private http: HttpClient) {}

  load(page = 1, pageSize = 20): Observable<AiInsightsResponse> {
    console.log(`Loading AI insights from: ${this.api}?page=${page}&pageSize=${pageSize}`);

    return this.http
      .get<AiInsightsResponse>(
        `${this.api}?page=${page}&pageSize=${pageSize}`)
      .pipe(
        tap(res => {
          console.log(`AI Insights API response:`, res);
          this._insights$.next(res.items);
          this._unreadCount$.next(res.unreadCount);
        })
      );
  }

  loadUnreadCount(): Observable<{ count: number }> {
    return this.http
      .get<{ count: number }>(`${this.api}/unread-count`)
      .pipe(tap(res => {
        this._unreadCount$.next(res.count);
      }));
  }

  pushRealTime(raw: any): void {
    const insight: AiInsight = {
      id:                raw.id,
      type:              raw.type,
      severity:          raw.severity,
      message:           raw.message,
      companyId:         raw.companyId,
      relatedJobId:      raw.relatedJobId   ?? undefined,
      relatedUserId:     raw.relatedUserId  ?? undefined,
      relatedJobTitle:   raw.relatedJobTitle  ?? undefined,
      relatedWorkerName: raw.relatedWorkerName ?? undefined,
      createdAt:         raw.createdAt,
      isRead:            false
    };
    console.log('Pushing real-time insight to panel:', insight);
    this._insights$.next([insight, ...this._insights$.getValue()]);
    this._unreadCount$.next(this._unreadCount$.getValue() + 1);
  }

  markAsRead(id: string): Observable<any> {
    return this.http.post(`${this.api}/mark-as-read`, { id }).pipe(
      tap(() => {
        this._insights$.next(
          this._insights$.getValue().map(i =>
            i.id === id ? { ...i, isRead: true } : i
          )
        );
        this._unreadCount$.next(
          Math.max(0, this._unreadCount$.getValue() - 1)
        );
      })
    );
  }

  markAllRead(): Observable<any> {
    return this.http.post(`${this.api}/mark-all-read`, {}).pipe(
      tap(() => {
        this._insights$.next(
          this._insights$.getValue().map(i => ({ ...i, isRead: true }))
        );
        this._unreadCount$.next(0);
      })
    );
  }

  runNow(): Observable<any> {
    console.log('Triggering AI check run...');
    return this.http.post(`${this.api}/run-now`, {});
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${this.api}/${id}`).pipe(
      tap(() => {
        const target  = this._insights$.getValue().find(i => i.id === id);
        const updated = this._insights$.getValue().filter(i => i.id !== id);
        this._insights$.next(updated);
        if (target && !target.isRead) {
          this._unreadCount$.next(
            Math.max(0, this._unreadCount$.getValue() - 1)
          );
        }
      })
    );
  }

  getUnreadCount(): number { return this._unreadCount$.getValue(); }
  getInsights():    AiInsight[] { return this._insights$.getValue(); }
}